/**
 * CreditAuditLineageModal
 *
 * CC-211-AUDIT Phase 2I: Full-screen modal viewer for a single Credit
 * Completeness snapshot's lineage trinity (snapshot + event + trace correlation).
 *
 * Opened from AdminCreditAuditPanel via row click in the snapshot table.
 * Fetches lineage on mount via adminCreditAuditService.getCreditCompletenessAudit.
 *
 * Contract: renders the full server payload per §31.12.5 F-5 test (lineage_present,
 * degraded_reasons, snapshot, event). inputs_echo contains UUIDs only — no PII rendered.
 */

import { FC, useEffect, useState, useCallback } from 'react';
import { FiX, FiCopy, FiCheck, FiAlertTriangle, FiDownload } from 'react-icons/fi';
import {
  getCreditCompletenessAudit,
  CreditCompletenessLineageResponse,
  AdminCreditAuditError,
} from '../../services/adminCreditAuditService';

export type LineageTabKey = 'scores' | 'formula' | 'intermediate' | 'inputs' | 'reasons' | 'raw';

const TAB_ORDER: { id: LineageTabKey; label: string }[] = [
  { id: 'scores', label: 'Scores' },
  { id: 'formula', label: 'Formula' },
  { id: 'intermediate', label: 'Intermediate' },
  { id: 'inputs', label: 'Inputs' },
  { id: 'reasons', label: 'Reasons' },
  { id: 'raw', label: 'Raw JSON' },
];

const RAW_JSON_BYTE_LIMIT = 200_000;

interface CreditAuditLineageModalProps {
  isOpen: boolean;
  memberId: string;
  snapshotId: string;
  onClose: () => void;
}

const CopyButton: FC<{ value: string; label?: string }> = ({ value, label }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = useCallback(() => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  }, [value]);
  return (
    <button
      type="button"
      onClick={onCopy}
      className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-blue-600 transition-colors"
      aria-label={label ? `Copy ${label}` : 'Copy value'}
    >
      {copied ? <FiCheck size={12} className="text-green-600" /> : <FiCopy size={12} />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

const SeverityBadge: FC<{ severity: string }> = ({ severity }) => {
  const styles: Record<string, string> = {
    action: 'bg-orange-100 text-orange-800 border-orange-200',
    warning: 'bg-yellow-100 text-yellow-800 border-yellow-200',
    info: 'bg-blue-100 text-blue-800 border-blue-200',
  };
  const cls = styles[severity] || 'bg-slate-100 text-slate-700 border-slate-200';
  return <span className={`inline-block px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide rounded border ${cls}`}>{severity}</span>;
};

const CreditAuditLineageModal: FC<CreditAuditLineageModalProps> = ({ isOpen, memberId, snapshotId, onClose }) => {
  const [data, setData] = useState<CreditCompletenessLineageResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<LineageTabKey>('scores');

  useEffect(() => {
    if (!isOpen || !memberId || !snapshotId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData(null);
    setActiveTab('scores');
    getCreditCompletenessAudit(memberId, snapshotId)
      .then((resp) => { if (!cancelled) setData(resp); })
      .catch((err: unknown) => {
        if (cancelled) return;
        const msg = err instanceof AdminCreditAuditError
          ? `${err.status === 404 ? 'Snapshot not found' : err.reason || err.message}`
          : err instanceof Error ? err.message : 'Unknown error loading lineage';
        setError(msg);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [isOpen, memberId, snapshotId]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const rawJsonString = data ? JSON.stringify(data, null, 2) : '';
  const rawJsonTooLarge = rawJsonString.length > RAW_JSON_BYTE_LIMIT;

  const downloadRawJson = () => {
    if (!data) return;
    const blob = new Blob([rawJsonString], { type: 'application/json' });
    const href = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = href;
    a.download = `lineage_${snapshotId}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(href);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-stretch bg-black/50" role="dialog" aria-modal="true" aria-label="Credit Completeness Lineage">
      <div className="flex flex-col w-full h-full bg-white shadow-2xl overflow-hidden">
        <ModalHeader
          memberId={memberId}
          snapshotId={snapshotId}
          data={data}
          onClose={onClose}
        />
        {loading && (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
              <p className="text-slate-600 text-sm">Loading lineage…</p>
            </div>
          </div>
        )}
        {error && !loading && (
          <div className="flex-1 flex items-center justify-center p-8">
            <div className="max-w-md text-center">
              <FiAlertTriangle className="w-10 h-10 text-red-500 mx-auto mb-3" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">Could not load lineage</h3>
              <p className="text-sm text-slate-600 mb-4">{error}</p>
              <button onClick={onClose} className="px-4 py-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 text-sm">Close</button>
            </div>
          </div>
        )}
        {data && !loading && !error && (
          <ModalBody
            data={data}
            activeTab={activeTab}
            setActiveTab={setActiveTab}
            rawJsonString={rawJsonString}
            rawJsonTooLarge={rawJsonTooLarge}
            onDownloadRaw={downloadRawJson}
          />
        )}
      </div>
    </div>
  );
};

export default CreditAuditLineageModal;

// ============================================================================
// Subcomponents — kept in same file for cohesion; no external imports add needed
// ============================================================================

interface ModalHeaderProps {
  memberId: string;
  snapshotId: string;
  data: CreditCompletenessLineageResponse | null;
  onClose: () => void;
}

const ModalHeader: FC<ModalHeaderProps> = ({ memberId, snapshotId, data, onClose }) => (
  <div className="flex-shrink-0 border-b border-slate-200 bg-slate-50 px-6 py-4">
    <div className="flex items-start justify-between gap-4">
      <div className="flex-1 min-w-0">
        <h2 className="text-lg font-semibold text-slate-900">Credit Completeness — Snapshot Lineage</h2>
        <div className="mt-1 grid grid-cols-1 sm:grid-cols-3 gap-x-6 gap-y-1 text-xs text-slate-600">
          <div className="flex items-center gap-2 font-mono">
            <span className="text-slate-400">Member:</span>
            <span className="truncate">{memberId}</span>
            <CopyButton value={memberId} label="member id" />
          </div>
          <div className="flex items-center gap-2 font-mono">
            <span className="text-slate-400">Snapshot:</span>
            <span className="truncate">{snapshotId}</span>
            <CopyButton value={snapshotId} label="snapshot id" />
          </div>
          <div className="flex items-center gap-2 font-mono">
            <span className="text-slate-400">Trace:</span>
            <span className="truncate">{data?.snapshot.trace_id || '—'}</span>
            {data?.snapshot.trace_id && <CopyButton value={data.snapshot.trace_id} label="trace id" />}
          </div>
        </div>
      </div>
      <button onClick={onClose} className="flex-shrink-0 p-2 text-slate-500 hover:text-slate-900 hover:bg-slate-200 rounded-lg" aria-label="Close modal">
        <FiX size={20} />
      </button>
    </div>
  </div>
);

interface ModalBodyProps {
  data: CreditCompletenessLineageResponse;
  activeTab: LineageTabKey;
  setActiveTab: (t: LineageTabKey) => void;
  rawJsonString: string;
  rawJsonTooLarge: boolean;
  onDownloadRaw: () => void;
}

const ModalBody: FC<ModalBodyProps> = ({ data, activeTab, setActiveTab, rawJsonString, rawJsonTooLarge, onDownloadRaw }) => {
  const { snapshot, event, lineage_present, degraded_reasons } = data;
  const eventDisabledTabs: LineageTabKey[] = ['formula', 'intermediate'];
  return (
    <>
      {(!lineage_present || degraded_reasons.length > 0) && (
        <div className="flex-shrink-0 border-b border-yellow-200 bg-yellow-50 px-6 py-3 flex items-start gap-3">
          <FiAlertTriangle className="flex-shrink-0 w-5 h-5 text-yellow-600 mt-0.5" />
          <div className="text-sm text-yellow-800">
            {!lineage_present && <p className="font-medium">Lineage event not available for this snapshot (pre-Phase 2A/2B).</p>}
            {degraded_reasons.length > 0 && (
              <ul className="list-disc list-inside mt-1">
                {degraded_reasons.map((r, i) => <li key={i}>{r}</li>)}
              </ul>
            )}
          </div>
        </div>
      )}
      <div className="flex-shrink-0 border-b border-slate-200 px-6">
        <div className="flex gap-1">
          {TAB_ORDER.map(tab => {
            const disabled = !event && eventDisabledTabs.includes(tab.id);
            return (
              <button
                key={tab.id}
                onClick={() => !disabled && setActiveTab(tab.id)}
                disabled={disabled}
                className={`px-4 py-2 text-sm font-medium transition-colors border-b-2 -mb-px ${
                  disabled
                    ? 'text-slate-300 border-transparent cursor-not-allowed'
                    : activeTab === tab.id
                      ? 'text-blue-600 border-blue-600'
                      : 'text-slate-500 border-transparent hover:text-slate-700'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>
      </div>
      <div className="flex-1 overflow-auto p-6">
        {activeTab === 'scores' && <ScoresTab snapshot={snapshot} event={event} />}
        {activeTab === 'formula' && event && <JsonBlock title="Formula Weights" data={event.formula_weights} />}
        {activeTab === 'intermediate' && event && <JsonBlock title="Intermediate Calculations" data={event.intermediate} />}
        {activeTab === 'inputs' && <InputsTab snapshot={snapshot} event={event} />}
        {activeTab === 'reasons' && <ReasonsTab snapshot={snapshot} />}
        {activeTab === 'raw' && (
          <RawJsonTab
            rawJsonString={rawJsonString}
            rawJsonTooLarge={rawJsonTooLarge}
            onDownload={onDownloadRaw}
          />
        )}
      </div>
    </>
  );
};

const ScoresTab: FC<{ snapshot: CreditCompletenessLineageResponse['snapshot']; event: CreditCompletenessLineageResponse['event'] }> = ({ snapshot, event }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
      <ScoreCard label="Overall" value={snapshot.overall_score} />
      <ScoreCard label="Coverage" value={snapshot.coverage_score} />
      <ScoreCard label="Profile" value={snapshot.profile_score} />
    </div>
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
      <MetaField label="Model" value={snapshot.model_version} mono />
      <MetaField label="Pipeline" value={snapshot.scoring_pipeline_version} mono />
      <MetaField label="Created" value={new Date(snapshot.created_at).toLocaleString()} />
      <MetaField label="Compute" value={event?.compute_ms != null ? `${event.compute_ms} ms` : '—'} />
    </div>
    <div>
      <p className="text-xs text-slate-400 mb-1 uppercase tracking-wide">Input Hash (SHA-256)</p>
      <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded px-3 py-2 font-mono text-xs break-all">
        <span className="flex-1">{snapshot.input_hash}</span>
        <CopyButton value={snapshot.input_hash} label="input hash" />
      </div>
    </div>
  </div>
);

const ScoreCard: FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="rounded-lg border border-slate-200 bg-white p-4 text-center">
    <p className="text-xs font-medium text-slate-500 uppercase tracking-wide">{label}</p>
    <p className="text-3xl font-bold text-slate-900 mt-1">{value.toFixed(2)}</p>
  </div>
);

const MetaField: FC<{ label: string; value: string; mono?: boolean }> = ({ label, value, mono }) => (
  <div>
    <p className="text-xs text-slate-400 uppercase tracking-wide">{label}</p>
    <p className={`text-sm text-slate-900 ${mono ? 'font-mono' : ''}`}>{value}</p>
  </div>
);

const InputsTab: FC<{ snapshot: CreditCompletenessLineageResponse['snapshot']; event: CreditCompletenessLineageResponse['event'] }> = ({ snapshot, event }) => {
  const echo = event?.inputs_echo;
  return (
    <div className="space-y-6">
      <section>
        <h3 className="text-sm font-semibold text-slate-900 mb-2">Snapshot Input Hash</h3>
        <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded px-3 py-2 font-mono text-xs break-all">
          <span className="flex-1">{snapshot.input_hash}</span>
          <CopyButton value={snapshot.input_hash} label="input hash" />
        </div>
        <p className="text-xs text-slate-500 mt-1">SHA-256 over the canonical input fixture used to compute this snapshot.</p>
      </section>
      {echo ? (
        <section>
          <h3 className="text-sm font-semibold text-slate-900 mb-2">Inputs Echo (UUIDs only — no PII)</h3>
          <JsonBlock title="" data={echo} />
        </section>
      ) : (
        <p className="text-sm text-slate-500">No inputs echo available for this snapshot.</p>
      )}
    </div>
  );
};

const ReasonsTab: FC<{ snapshot: CreditCompletenessLineageResponse['snapshot'] }> = ({ snapshot }) => {
  const reasons = snapshot.reasons || [];
  if (reasons.length === 0) {
    return <p className="text-sm text-slate-500">No reason codes recorded for this snapshot.</p>;
  }
  return (
    <div className="space-y-2">
      {reasons.map((r, i) => (
        <div key={`${r.code}-${i}`} className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-3">
          <SeverityBadge severity={r.severity} />
          <div className="flex-1 min-w-0">
            <span className="font-mono text-xs text-slate-700">{r.code}</span>
            <p className="text-sm text-slate-900 mt-1">{r.text}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

const JsonBlock: FC<{ title: string; data: unknown }> = ({ title, data }) => {
  const text = JSON.stringify(data, null, 2);
  return (
    <div>
      {title && (
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-slate-900">{title}</h3>
          <CopyButton value={text} label={title} />
        </div>
      )}
      <pre className="bg-slate-900 text-slate-100 text-xs font-mono p-4 rounded-lg overflow-auto max-h-[60vh]">{text}</pre>
    </div>
  );
};

const RawJsonTab: FC<{ rawJsonString: string; rawJsonTooLarge: boolean; onDownload: () => void }> = ({ rawJsonString, rawJsonTooLarge, onDownload }) => (
  <div>
    <div className="flex items-center justify-between mb-2">
      <h3 className="text-sm font-semibold text-slate-900">Full Lineage Payload</h3>
      <div className="flex items-center gap-3">
        <CopyButton value={rawJsonString} label="raw json" />
        <button
          type="button"
          onClick={onDownload}
          className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-blue-600"
        >
          <FiDownload size={12} /> Download
        </button>
      </div>
    </div>
    {rawJsonTooLarge ? (
      <div className="rounded-lg border border-yellow-200 bg-yellow-50 p-4 text-sm text-yellow-800">
        Payload exceeds {Math.round(RAW_JSON_BYTE_LIMIT / 1000)}&nbsp;KB — use Download to save the full JSON locally.
      </div>
    ) : (
      <pre className="bg-slate-900 text-slate-100 text-xs font-mono p-4 rounded-lg overflow-auto max-h-[65vh]">{rawJsonString}</pre>
    )}
  </div>
);
